import { useMemo } from 'react'
import { Label, Paragraph, SearchAutocomplete } from '@quickmd-llc/design-system'
import type { MedicationStaffApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { MedicationCard } from './MedicationCard'

interface MedicationListPanelProps {
  medications: MedicationStaffApiOutContract[]
  selectedId: string | null
  search: string
  onSearchChange: (value: string) => void
  onSelect: (medication: MedicationStaffApiOutContract) => void
}

export function MedicationListPanel({
  medications,
  selectedId,
  search,
  onSearchChange,
  onSelect,
}: MedicationListPanelProps) {
  const filtered = useMemo(() => {
    if (!search) return medications
    const term = search.toLowerCase()
    return medications.filter(
      (m) =>
        (m.Name ?? '').toLowerCase().includes(term) ||
        (m.NationalDrugCode ?? '').toLowerCase().includes(term) ||
        (m.GenericCodeNumber ?? '').toLowerCase().includes(term),
    )
  }, [medications, search])

  return (
    <div className='flex min-h-0 flex-col rounded-large border border-border-medium bg-white'>
      {/* Search */}
      <div className='p-sp-2'>
        <SearchAutocomplete
          placeholder='Search by name or drug code'
          value={search}
          onChange={onSearchChange}
          showResults={false}
        />
      </div>

      {/* Count */}
      <div className='border-b border-border-medium px-sp-2 py-sp-1'>
        <Label size='lg'>
          {filtered.length} {filtered.length === 1 ? 'medication' : 'medications'}
        </Label>
      </div>

      {/* List */}
      <div className='flex-1 overflow-y-auto rounded-b-large'>
        {filtered.length === 0 ?
          <div className='flex h-full items-center justify-center p-sp-2'>
            <Paragraph size='sm' className='text-center text-text-light'>
              {search ? 'No medications match your search.' : 'No medications yet.'}
            </Paragraph>
          </div>
        : <div className='flex flex-col gap-sp-1 p-sp-1'>
            {filtered.map((med) => (
              <MedicationCard key={med.Id} medication={med} isSelected={med.Id === selectedId} onClick={onSelect} />
            ))}
          </div>
        }
      </div>
    </div>
  )
}
